import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Switch,
  I18nManager,
  TextInput,
  Modal,
  Pressable,
} from 'react-native';
import * as ExpoClipboard from 'expo-clipboard';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import twoFactorService, { TwoFactorStatus } from '../services/twoFactorService';
import logger from '../utils/logger';

export default function TwoFactorSettingsScreen({ navigation }: any) {
  const { t } = useTranslation();
  const { theme, isDark } = useTheme();
  const isRTL = I18nManager.isRTL;

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  // BUG-002: cross-platform modal state (replaces Alert.prompt which is iOS-only)
  const [showDisableTotpModal, setShowDisableTotpModal] = useState(false);
  const [disableTotpPassword, setDisableTotpPassword] = useState('');
  // BUG-012: prevent concurrent load calls (setTimeout + initial load race condition)
  const isLoadingRef = useRef(false);

  useEffect(() => {
    loadStatus();

    // Reload status when navigating back to this screen
    const unsubscribe = navigation.addListener('focus', () => {
      logger.log('🔄 TwoFactorSettingsScreen focused - reloading status...');
      loadStatus(); // isLoadingRef guard prevents duplicate calls
    });

    return unsubscribe;
  }, [navigation]);

  const loadStatus = async () => {
    // BUG-012: skip if a load is already in progress
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      logger.log('📥 Loading 2FA status...');
      setLoading(true);
      const data = await twoFactorService.getStatus();
      logger.log('✅ 2FA status loaded:', {
        totpEnabled: data.totpEnabled,
        emailOtpEnabled: data.emailOtpEnabled,
        raw: data,
      });
      logger.log('🔑 Email OTP Status:', data.emailOtpEnabled ? 'ENABLED ✅' : 'DISABLED ❌');
      setStatus(data);
    } catch (error: any) {
      logger.error('❌ Failed to load 2FA status:', error);
      Alert.alert(
        isRTL ? 'خطأ' : 'Error',
        error.message || (isRTL ? 'فشل تحميل حالة المصادقة الثنائية' : 'Failed to load 2FA status'),
      );
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  const handleToggleTotp = async (enabled: boolean) => {
    if (enabled) {
      // Navigate to setup screen
      navigation.navigate('TwoFactorSetup');
    } else {
      // BUG-002: replaced iOS-only Alert.prompt with cross-platform Modal
      setDisableTotpPassword('');
      setShowDisableTotpModal(true);
    }
  };

  const handleDisableTotp = async () => {
    if (!disableTotpPassword.trim()) {
      Alert.alert(isRTL ? 'خطأ' : 'Error', isRTL ? 'كلمة المرور مطلوبة' : 'Password is required');
      return;
    }
    setShowDisableTotpModal(false);
    try {
      setToggling('totp');
      await twoFactorService.disableTotp(disableTotpPassword);
      await loadStatus();
      Alert.alert(
        isRTL ? 'نجح' : 'Success',
        isRTL ? 'تم تعطيل المصادقة الثنائية' : '2FA disabled successfully',
      );
    } catch (error: any) {
      Alert.alert(
        isRTL ? 'خطأ' : 'Error',
        error.message || (isRTL ? 'فشل تعطيل المصادقة الثنائية' : 'Failed to disable 2FA'),
      );
    } finally {
      setToggling(null);
    }
  };

  const handleToggleEmailOtp = async (enabled: boolean) => {
    logger.log('🔄 handleToggleEmailOtp called, enabled:', enabled);
    if (enabled) {
      // Navigate to email OTP verification screen
      logger.log('➡️ Navigating to EmailOtpVerify screen...');
      navigation.navigate('EmailOtpVerify');
    } else {
      // Disable Email OTP
      Alert.alert(
        isRTL ? 'تأكيد' : 'Confirm',
        isRTL
          ? 'هل أنت متأكد من تعطيل رمز التحقق بالبريد؟'
          : 'Are you sure you want to disable email OTP?',
        [
          { text: isRTL ? 'إلغاء' : 'Cancel', style: 'cancel' },
          {
            text: isRTL ? 'تعطيل' : 'Disable',
            style: 'destructive',
            onPress: async () => {
              try {
                setToggling('email');
                await twoFactorService.disableEmailOtp();
                await loadStatus();
                Alert.alert(
                  isRTL ? 'نجح' : 'Success',
                  isRTL ? 'تم تعطيل رمز التحقق بالبريد' : 'Email OTP disabled successfully',
                );
              } catch (error: any) {
                Alert.alert(
                  isRTL ? 'خطأ' : 'Error',
                  error.message ||
                    (isRTL ? 'فشل تعطيل رمز التحقق بالبريد' : 'Failed to disable email OTP'),
                );
              } finally {
                setToggling(null);
              }
            },
          },
        ],
      );
    }
  };

  const handleRegenerateBackupCodes = async () => {
    Alert.alert(
      isRTL ? 'تأكيد' : 'Confirm',
      isRTL
        ? 'سيؤدي هذا إلى إبطال جميع رموز الاحتياطي القديمة. هل تريد المتابعة؟'
        : 'This will invalidate all old backup codes. Do you want to continue?',
      [
        { text: isRTL ? 'إلغاء' : 'Cancel', style: 'cancel' },
        {
          text: isRTL ? 'نعم' : 'Yes',
          onPress: async () => {
            try {
              const response = await twoFactorService.regenerateBackupCodes();

              // Show backup codes in alert
              const codesText = response.backupCodes.join('\n');
              Alert.alert(
                isRTL ? 'رموز احتياطية جديدة' : 'New Backup Codes',
                isRTL
                  ? `احفظ هذه الرموز في مكان آمن:\n\n${codesText}`
                  : `Save these codes in a safe place:\n\n${codesText}`,
                [
                  {
                    text: isRTL ? 'نسخ الكل' : 'Copy All',
                    // BUG-005: replaced deprecated Clipboard from react-native with expo-clipboard
                    onPress: async () => {
                      await ExpoClipboard.setStringAsync(codesText);
                    },
                  },
                  { text: isRTL ? 'حسناً' : 'OK' },
                ],
              );

              await loadStatus();
            } catch (error: any) {
              Alert.alert(
                isRTL ? 'خطأ' : 'Error',
                error.message || (isRTL ? 'فشل إعادة توليد الرموز' : 'Failed to regenerate codes'),
              );
            }
          },
        },
      ],
    );
  };

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons
            name={isRTL ? 'chevron-forward' : 'chevron-back'}
            size={28}
            color={theme.colors.text}
          />
        </TouchableOpacity>
        <Text style={[styles.title, { color: theme.colors.text }]}>
          {isRTL ? 'إعدادات المصادقة الثنائية' : '2FA Settings'}
        </Text>
      </View>

      {/* Info Banner */}
      <View style={[styles.infoBanner, { backgroundColor: theme.colors.primary + '15' }]}>
        <Ionicons name="shield-checkmark" size={24} color={theme.colors.primary} />
        <View style={{ flex: 1 }}>
          <Text style={[styles.infoTitle, { color: theme.colors.text }]}>
            {isRTL ? 'المصادقة الثنائية' : 'Two-Factor Authentication'}
          </Text>
          <Text style={[styles.infoText, { color: theme.colors.textSecondary }]}>
            {isRTL
              ? 'أضف طبقة حماية إضافية لحسابك'
              : 'Add an extra layer of security to your account'}
          </Text>
        </View>
      </View>

      {/* TOTP Section */}
      <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
        <View style={styles.cardHeader}>
          <View style={styles.cardHeaderLeft}>
            <Ionicons name="phone-portrait" size={24} color={theme.colors.text} />
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
                {isRTL ? 'تطبيق المصادقة' : 'Authenticator App'}
              </Text>
              <Text style={[styles.cardDescription, { color: theme.colors.textSecondary }]}>
                {isRTL
                  ? 'استخدم Google Authenticator أو تطبيق مماثل'
                  : 'Use Google Authenticator or similar app'}
              </Text>
            </View>
          </View>
          {toggling === 'totp' ? (
            <ActivityIndicator color={theme.colors.primary} />
          ) : (
            <Switch
              value={status?.totpEnabled || false}
              onValueChange={handleToggleTotp}
              trackColor={{ false: theme.colors.border, true: theme.colors.primary }}
              thumbColor="#fff"
              accessible={true}
              accessibilityLabel={
                isRTL
                  ? `تطبيق المصادقة: ${status?.totpEnabled ? 'مفعل' : 'معطل'}`
                  : `Authenticator App: ${status?.totpEnabled ? 'Enabled' : 'Disabled'}`
              }
              accessibilityHint={
                isRTL
                  ? status?.totpEnabled
                    ? 'انقر نقرًا مزدوجًا لتعطيل تطبيق المصادقة'
                    : 'انقر نقرًا مزدوجًا لتفعيل تطبيق المصادقة'
                  : status?.totpEnabled
                  ? 'Double tap to disable authenticator app'
                  : 'Double tap to enable authenticator app'
              }
              accessibilityRole="switch"
            />
          )}
        </View>

        {status?.totpEnabled && (
          <View style={[styles.statusBox, { backgroundColor: theme.colors.background }]}>
            <Ionicons name="checkmark-circle" size={20} color="#4CAF50" />
            <Text style={[styles.statusText, { color: theme.colors.text }]}>
              {isRTL ? 'مفعّل منذ' : 'Enabled since'}{' '}
              {status.totpSetupAt
                ? new Date(status.totpSetupAt).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')
                : '-'}
            </Text>
          </View>
        )}
      </View>

      {/* Email OTP Section */}
      <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
        <View style={styles.cardHeader}>
          <View style={styles.cardHeaderLeft}>
            <Ionicons name="mail" size={24} color={theme.colors.text} />
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
                {isRTL ? 'رمز التحقق بالبريد' : 'Email Verification'}
              </Text>
              <Text style={[styles.cardDescription, { color: theme.colors.textSecondary }]}>
                {isRTL
                  ? 'احصل على رمز التحقق عبر البريد الإلكتروني'
                  : 'Receive verification codes via email'}
              </Text>
            </View>
          </View>
          {toggling === 'email' ? (
            <ActivityIndicator color={theme.colors.primary} />
          ) : (
            <Switch
              value={status?.emailOtpEnabled || false}
              onValueChange={handleToggleEmailOtp}
              trackColor={{ false: theme.colors.border, true: theme.colors.primary }}
              thumbColor="#fff"
              accessible={true}
              accessibilityLabel={
                isRTL
                  ? `رمز التحقق بالبريد: ${status?.emailOtpEnabled ? 'مفعل' : 'معطل'}`
                  : `Email Verification: ${status?.emailOtpEnabled ? 'Enabled' : 'Disabled'}`
              }
              accessibilityHint={
                isRTL
                  ? status?.emailOtpEnabled
                    ? 'انقر نقرًا مزدوجًا لتعطيل رمز التحقق بالبريد'
                    : 'انقر نقرًا مزدوجًا لتفعيل رمز التحقق بالبريد'
                  : status?.emailOtpEnabled
                  ? 'Double tap to disable email verification'
                  : 'Double tap to enable email verification'
              }
              accessibilityRole="switch"
            />
          )}
        </View>

        {status?.emailOtpEnabled && (
          <View style={[styles.statusBox, { backgroundColor: theme.colors.background }]}>
            <Ionicons name="checkmark-circle" size={20} color="#4CAF50" />
            <Text style={[styles.statusText, { color: theme.colors.text }]}>
              {isRTL ? 'مفعّل منذ' : 'Enabled since'}{' '}
              {status.emailOtpSetupAt
                ? new Date(status.emailOtpSetupAt).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')
                : '-'}
            </Text>
          </View>
        )}
      </View>

      {/* Backup Codes Section */}
      {status?.totpEnabled && (
        <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
          <View style={styles.cardHeader}>
            <View style={styles.cardHeaderLeft}>
              <Ionicons name="key" size={24} color={theme.colors.text} />
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
                  {isRTL ? 'رموز الاحتياطي' : 'Backup Codes'}
                </Text>
                <Text style={[styles.cardDescription, { color: theme.colors.textSecondary }]}>
                  {isRTL
                    ? `متبقي ${status.backupCodesRemaining} من 10 رموز`
                    : `${status.backupCodesRemaining} of 10 codes remaining`}
                </Text>
              </View>
            </View>
          </View>

          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: theme.colors.background }]}
            onPress={handleRegenerateBackupCodes}
            accessible={true}
            accessibilityLabel={isRTL ? 'إعادة توليد رموز الاحتياطي' : 'Regenerate Backup Codes'}
            accessibilityHint={
              isRTL
                ? 'انقر نقرًا مزدوجًا لإعادة توليد رموز الاحتياطي الجديدة (ستؤدي للبطلان جميع الرموز القديمة)'
                : 'Double tap to regenerate new backup codes (invalidates old codes)'
            }
            accessibilityRole="button"
          >
            <Ionicons name="refresh" size={20} color={theme.colors.primary} />
            <Text style={[styles.actionButtonText, { color: theme.colors.primary }]}>
              {isRTL ? 'إعادة توليد الرموز' : 'Regenerate Codes'}
            </Text>
          </TouchableOpacity>

          <View style={[styles.warningBox, { backgroundColor: 'rgba(255, 152, 0, 0.1)' }]}>
            <Ionicons name="warning" size={18} color="#FF9800" />
            <Text style={[styles.warningText, { color: '#FF9800' }]}>
              {isRTL
                ? 'احفظ رموز الاحتياطي في مكان آمن. كل رمز يستخدم مرة واحدة فقط.'
                : 'Keep backup codes safe. Each code can only be used once.'}
            </Text>
          </View>
        </View>
      )}

      {/* BUG-002: cross-platform Disable TOTP Modal (replaces iOS-only Alert.prompt) */}
      <Modal
        visible={showDisableTotpModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDisableTotpModal(false)}
      >
        <Pressable style={styles.modalOverlay} onPress={() => setShowDisableTotpModal(false)}>
          <Pressable
            style={[styles.modalContent, { backgroundColor: theme.colors.card }]}
            onPress={() => {}}
          >
            <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
              {isRTL ? 'تأكيد كلمة المرور' : 'Confirm Password'}
            </Text>
            <Text style={[styles.modalSubtitle, { color: theme.colors.textSecondary }]}>
              {isRTL
                ? 'يرجى إدخال كلمة المرور لتعطيل المصادقة الثنائية'
                : 'Enter your password to disable 2FA'}
            </Text>
            <TextInput
              style={[
                styles.modalInput,
                {
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                  backgroundColor: theme.colors.background,
                },
              ]}
              secureTextEntry
              placeholder={isRTL ? 'كلمة المرور' : 'Password'}
              placeholderTextColor={theme.colors.textSecondary}
              value={disableTotpPassword}
              onChangeText={setDisableTotpPassword}
              autoFocus
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, { borderColor: theme.colors.border }]}
                onPress={() => setShowDisableTotpModal(false)}
              >
                <Text style={[styles.modalButtonText, { color: theme.colors.text }]}>
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonDestructive]}
                onPress={handleDisableTotp}
              >
                <Text style={[styles.modalButtonText, { color: '#fff' }]}>
                  {isRTL ? 'تعطيل' : 'Disable'}
                </Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Security Tips */}
      <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
        <Text style={[styles.tipsTitle, { color: theme.colors.text }]}>
          {isRTL ? 'نصائح الأمان' : 'Security Tips'}
        </Text>

        <View style={styles.tip}>
          <Ionicons name="checkmark-circle" size={20} color={theme.colors.primary} />
          <Text style={[styles.tipText, { color: theme.colors.textSecondary }]}>
            {isRTL
              ? 'استخدم تطبيق مصادقة بدلاً من الرسائل النصية'
              : 'Use an authenticator app instead of SMS'}
          </Text>
        </View>

        <View style={styles.tip}>
          <Ionicons name="checkmark-circle" size={20} color={theme.colors.primary} />
          <Text style={[styles.tipText, { color: theme.colors.textSecondary }]}>
            {isRTL
              ? 'احفظ رموز الاحتياطي في مكان آمن ومنفصل'
              : 'Store backup codes in a safe, separate location'}
          </Text>
        </View>

        <View style={styles.tip}>
          <Ionicons name="checkmark-circle" size={20} color={theme.colors.primary} />
          <Text style={[styles.tipText, { color: theme.colors.textSecondary }]}>
            {isRTL
              ? 'لا تشارك رموز التحقق مع أي شخص'
              : 'Never share verification codes with anyone'}
          </Text>
        </View>

        <View style={styles.tip}>
          <Ionicons name="checkmark-circle" size={20} color={theme.colors.primary} />
          <Text style={[styles.tipText, { color: theme.colors.textSecondary }]}>
            {isRTL ? 'راجع أجهزتك الموثوقة بانتظام' : 'Review your trusted devices regularly'}
          </Text>
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    paddingTop: 60,
  },
  backButton: {
    marginRight: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    margin: 16,
    padding: 16,
    borderRadius: 12,
    gap: 12,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  infoText: {
    fontSize: 13,
    lineHeight: 18,
  },
  card: {
    margin: 16,
    marginTop: 0,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  cardDescription: {
    fontSize: 13,
    lineHeight: 18,
  },
  statusBox: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    gap: 8,
  },
  statusText: {
    fontSize: 13,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    padding: 12,
    borderRadius: 8,
    gap: 8,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    gap: 8,
  },
  warningText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 18,
  },
  tipsTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 16,
  },
  tip: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
    gap: 12,
  },
  tipText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 20,
  },
  // BUG-002: styles for the cross-platform disable TOTP modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalContent: {
    width: '100%',
    borderRadius: 16,
    padding: 24,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  modalSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 20,
  },
  modalInput: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    marginBottom: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center' as const,
    borderWidth: 1,
  },
  modalButtonDestructive: {
    backgroundColor: '#ef4444',
    borderColor: '#ef4444',
  },
  modalButtonText: {
    fontSize: 15,
    fontWeight: '600' as const,
  },
});
